// app/(auth)/login.tsx
import AppScreen from "@/components/default/AppScreen";
import { Button, Input } from "@/components/default/forms";
import { useAppTheme } from "@/hooks/useAppTheme";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { clearError, login } from "@/store/slices/authSlice";
import { router } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { useEffect, useState } from "react";
import {
  Alert,
  Dimensions,
  Image,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";

const { width } = Dimensions.get("window");

export default function LoginScreen() {
  const { colors, spacing, mode } = useAppTheme();
  const dispatch = useAppDispatch();
  const { loading, error } = useAppSelector((state) => state.auth);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({ email: "", password: "" });

  // Clear Redux error when user types
  useEffect(() => {
    if (error) dispatch(clearError());
  }, [email, password, error]);

  // Show API error as alert when it changes
  useEffect(() => {
    if (error) {
      Alert.alert("Login Failed", error);
    }
  }, [error]);

  const validate = () => {
    let valid = true;
    const newErrors = { email: "", password: "" };

    if (!email.trim()) {
      newErrors.email = "Email is required";
      valid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Enter a valid email";
      valid = false;
    }

    if (!password) {
      newErrors.password = "Password is required";
      valid = false;
    } else if (password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleLogin = async () => {
    if (!validate()) return;
    const result = await dispatch(login({ email, password }));
    if (login.fulfilled.match(result)) {
      router.replace("/(app)/dashboard");
    }
    // Error is handled by the useEffect above
  };

  const handleForgotPassword = () => {
    Alert.alert("Forgot Password", "Feature will comming soon");
  };

  const handleCreateAccount = () => {
    router.push("/(auth)/onboarding");
  };

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    keyboardView: {
      flex: 1,
    },
    scrollContent: {
      flexGrow: 1,
      justifyContent: "center",
      alignItems: "center",
      paddingVertical: spacing.xl,
    },
    card: {
      width: width * 0.9,
      maxWidth: 400,
      backgroundColor: colors.background,
      borderRadius: 16,
      paddingVertical: spacing.xxl,
      paddingHorizontal: spacing.xl,
      shadowColor: mode === "light" ? "#000" : "#fff",
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.05,
      shadowRadius: 8,
      elevation: 3,
      borderWidth: 1,
      borderColor: colors.border,
    },
    logoContainer: {
      alignItems: "center",
      marginBottom: spacing.xxl,
    },
    logo: {
      width: 80,
      height: 80,
      borderRadius: 40,
      marginBottom: spacing.md,
    },
    brandName: {
      fontSize: 24,
      fontWeight: "600",
      color: colors.text,
      letterSpacing: 0.5,
    },
    tagline: {
      fontSize: 14,
      color: colors.textLight,
      marginTop: spacing.xs,
    },
    inputContainer: {
      marginBottom: spacing.lg,
    },
    forgotLink: {
      alignSelf: "flex-end",
      marginTop: -spacing.sm,
      marginBottom: spacing.xl,
    },
    forgotText: {
      color: colors.primary,
      fontSize: 14,
      fontWeight: "500",
    },
    loginButton: {
      width: "100%",
      marginTop: spacing.sm,
    },
    divider: {
      flexDirection: "row",
      alignItems: "center",
      marginVertical: spacing.xl,
    },
    dividerLine: {
      flex: 1,
      height: 1,
      backgroundColor: colors.border,
    },
    dividerText: {
      marginHorizontal: spacing.md,
      color: colors.textLight,
      fontSize: 14,
    },
    createAccountButton: {
      alignItems: "center",
      paddingVertical: spacing.md,
    },
    createAccountText: {
      color: colors.primary,
      fontSize: 16,
      fontWeight: "500",
    },
  });

  return (
    <AppScreen>
      <View style={styles.container}>
        <StatusBar style={mode === "light" ? "dark" : "light"} />
        <KeyboardAvoidingView
          behavior={Platform.OS === "ios" ? "padding" : "height"}
          style={styles.keyboardView}
        >
          <ScrollView
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
          >
            <View style={styles.card}>
              <View style={styles.logoContainer}>
                <Image
                  source={require("@/assets/images/icon.png")}
                  style={styles.logo}
                />
                <Text style={styles.brandName}>Shree Setup</Text>
                <Text style={styles.tagline}>Sign in to continue</Text>
              </View>

              <Input
                label="Email"
                value={email}
                onChangeText={setEmail}
                placeholder="your@email.com"
                keyboardType="email-address"
                autoCapitalize="none"
                error={errors.email}
                containerStyle={styles.inputContainer}
              />

              <Input
                label="Password"
                value={password}
                onChangeText={setPassword}
                placeholder="Enter your password"
                secureTextEntry
                error={errors.password}
                containerStyle={styles.inputContainer}
              />

              <TouchableOpacity
                style={styles.forgotLink}
                onPress={handleForgotPassword}
                activeOpacity={0.7}
              >
                <Text style={styles.forgotText}>Forgot password?</Text>
              </TouchableOpacity>

              <Button
                title="Sign In"
                onPress={handleLogin}
                loading={loading}
                variant="primary"
                containerStyle={styles.loginButton}
              />

              {/* <View style={styles.divider}>
                <View style={styles.dividerLine} />
                <Text style={styles.dividerText}>or</Text>
                <View style={styles.dividerLine} />
              </View> */}

              {/* <TouchableOpacity
                style={styles.createAccountButton}
                onPress={handleCreateAccount}
                activeOpacity={0.7}
              >
                <Text style={styles.createAccountText}>
                  Create an organization account
                </Text>
              </TouchableOpacity> */}
            </View>
          </ScrollView>
        </KeyboardAvoidingView>
      </View>
    </AppScreen>
  );
}
